import api from '@/api';
import { resolveWebAppOrigin } from '@/auth/password-reset';

export type ShareType =
  | 'trove'
  | 'bookmark'
  | 'bookmarks'
  | 'group'
  | 'note'
  | 'notes'
  | 'notesGroup'
  | 'highlight'
  | 'highlights'
  | 'highlightsSession'
  | 'project'
  | 'assistantChat';
export type ShareRole = 'viewer' | 'editor';

export type ProjectSectionKey = 'tabs' | 'research' | 'bookmarks' | 'notes' | 'highlighter' | 'tasks';

export interface ShareMember {
  email: string;
  role: ShareRole;
}

export interface Share {
  id: string;
  ownerAid: string;
  ownerEmail: string;
  shareType: ShareType;
  troveId?: string;
  groupId?: string;
  noteId?: string;
  noteIds?: string[];
  notesGroupId?: string;
  highlightId?: string;
  highlightIds?: string[];
  highlightsSessionSid?: string;
  bookmarkId?: string;
  bookmarkIds?: string[];
  projectSid?: string;
  sections?: ProjectSectionKey[];
  ownerUid?: string;
  chatId?: string;
  snapshotAt?: number;
  name: string;
  publicLinkId: string;
  publicLinkEnabled: boolean;
  members: ShareMember[];
  createdAt: number;
  updatedAt: number;
}

export interface ShareWithRole extends Share {
  role?: ShareRole;
}

const base = '/shares';

export async function getSharesByOwner(
  troveId?: string,
  groupId?: string,
  noteId?: string,
  notesGroupId?: string,
  projectSid?: string,
  chatId?: string,
  highlightId?: string,
  highlightsSessionSid?: string
): Promise<{ shares: Share[]; share?: Share }> {
  const params: {
    troveId?: string;
    groupId?: string;
    noteId?: string;
    notesGroupId?: string;
    projectSid?: string;
    chatId?: string;
    highlightId?: string;
    highlightsSessionSid?: string;
  } = {};
  if (troveId) params.troveId = troveId;
  if (groupId) params.groupId = groupId;
  if (noteId) params.noteId = noteId;
  if (notesGroupId) params.notesGroupId = notesGroupId;
  if (projectSid) params.projectSid = projectSid;
  if (chatId) params.chatId = chatId;
  if (highlightId) params.highlightId = highlightId;
  if (highlightsSessionSid !== undefined) params.highlightsSessionSid = highlightsSessionSid;
  const { data } = await api.get<{ status: string; shares: Share[]; share?: Share }>(
    base,
    Object.keys(params).length ? { params } : undefined
  );
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to fetch shares');
  return { shares: data.shares ?? [], share: data.share };
}

export async function getShare(shareId: string): Promise<ShareWithRole> {
  const { data } = await api.get<{ status: string; share: Share; role?: ShareRole }>(`${base}/${shareId}`);
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to fetch share');
  return { ...data.share, role: data.role };
}

export async function getShareContent(shareId: string): Promise<{ share: Share; content: unknown[]; role?: ShareRole }> {
  const { data } = await api.get<{ status: string; share: Share; content?: unknown[]; role?: ShareRole }>(
    `${base}/${shareId}/content`
  );
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to load shared content');
  return { share: data.share!, content: data.content ?? [], role: data.role };
}

export async function createShare(params: {
  shareType: ShareType;
  troveId?: string;
  groupId?: string;
  noteId?: string;
  noteIds?: string[];
  notesGroupId?: string;
  highlightId?: string;
  highlightIds?: string[];
  highlightsSessionSid?: string;
  bookmarkId?: string;
  bookmarkIds?: string[];
  projectSid?: string;
  sections?: ProjectSectionKey[];
  chatId?: string;
  name: string;
  publicLinkEnabled?: boolean;
  members?: ShareMember[];
}): Promise<{ shareId: string; share: Share }> {
  const { data } = await api.post<{ status: string; shareId: string; share?: Share; message?: string }>(base, params);
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to create share');
  if (!data.shareId) throw new Error('No shareId returned');
  return { shareId: data.shareId, share: data.share! };
}

export async function updateShare(
  shareId: string,
  updates: { publicLinkEnabled?: boolean; members?: ShareMember[]; refreshSnapshot?: boolean }
): Promise<Share> {
  const { data } = await api.put<{ status: string; share: Share }>(`${base}/${shareId}`, updates);
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to update share');
  return data.share!;
}

export async function updateSharePermissions(
  shareId: string,
  email: string,
  role: ShareRole
): Promise<void> {
  const { data } = await api.put<{ status: string }>(`${base}/${shareId}/permissions`, { email, role });
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to update permissions');
}

export async function revokeMember(shareId: string, email: string): Promise<void> {
  const encodedEmail = encodeURIComponent(email);
  const { data } = await api.delete<{ status: string }>(`${base}/${shareId}/members/${encodedEmail}`);
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to revoke access');
}

export async function listSharedWithMe(): Promise<Array<{ shareId: string; role: string; share?: Share }>> {
  const { data } = await api.get<{ status: string; items?: Array<{ shareId: string; role: string; share?: Share }> }>(
    `${base}/shared-with-me`
  );
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to list shared items');
  return data.items ?? [];
}

export async function deleteShare(shareId: string): Promise<void> {
  const { data } = await api.delete<{ status: string }>(`${base}/${shareId}`);
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to delete share');
}

export async function copyShareToMyTrove(shareId: string): Promise<{ copiedCount: number }> {
  const { data } = await api.post<{ status: string; copiedCount?: number }>(`${base}/${shareId}/copy`);
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to copy shared items');
  return { copiedCount: data.copiedCount ?? 0 };
}

export function buildPublicShareUrl(publicLinkId: string): string {
  return `${resolveWebAppOrigin()}/t/${publicLinkId}`;
}

/** Fetch public share (no auth required). */
export async function getSharePublic(publicLinkId: string): Promise<{ share: Share; content: unknown[] }> {
  const { data } = await api.get<{ status: string; share: Share; content?: unknown[] }>(
    `${base}/public/${publicLinkId}`
  );
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to load share');
  return { share: data.share!, content: data.content ?? [] };
}
